import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  useTheme,
  useMediaQuery,
  Alert,
  CircularProgress,
  Chip,
  Typography,
  Button,
  Paper,
  Stack,
  IconButton,
  TextField,
  InputAdornment,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Search as SearchIcon, Campaign as CampaignIcon } from '@mui/icons-material';
import Header from '../components/Layout/Header';
import Sidebar from '../components/Layout/Sidebar';
import AnnouncementForm from '../components/Forms/AnnouncementForm';
import ConfirmationDialog from '../components/Common/ConfirmationDialog';
import { announcementService, roleService, userService } from '../services/apiService';
import { formatDate } from '../utils/dateFormat';
import { showToast } from '../utils/toast';

const Announcements = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleDrawerToggle = () => {
    setSidebarOpen((prev) => !prev);
  };
  const drawerWidth = 280;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [parents, setParents] = useState([]);
  const [expandedMessages, setExpandedMessages] = useState({});
  const [searchText, setSearchText] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({ open: false, announcement: null });

  const toggleMessage = (id) => {
    setExpandedMessages(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const refresh = useCallback(async () => {
    try {
      setLoading(true); setError(null);
      const res = await announcementService.getAll();
      setRows(res.data || res || []);
    } catch (err) { setError(err.message || 'Failed to load'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    (async () => {
      // Load users for target dropdowns
      const [rolesRes, usersRes] = await Promise.all([roleService.getAll(), userService.getAll()]);
      const teacherRole = (rolesRes.data || rolesRes)?.find(r => r.role_name?.toLowerCase() === 'teacher');
      const parentRole = (rolesRes.data || rolesRes)?.find(r => r.role_name?.toLowerCase() === 'parent');
      const allUsers = usersRes.data || usersRes || [];
      setTeachers(teacherRole ? allUsers.filter(u => u.role_id === teacherRole.id) : []);
      setParents(parentRole ? allUsers.filter(u => u.role_id === parentRole.id) : []);
      await refresh();
    })();
  }, [refresh]);

  const handleAdd = () => { setEditing(null); setModalOpen(true); };
  const handleEdit = (row) => { setEditing(row); setModalOpen(true); };
  const handleDelete = (row) => {
    setConfirmDialog({ open: true, announcement: row });
  };
  const handleDeleteConfirm = async () => {
    if (!confirmDialog.announcement) return;
    try {
      await announcementService.delete(confirmDialog.announcement.id);
      showToast.success('Announcement deleted successfully');
      await refresh();
      setConfirmDialog({ open: false, announcement: null });
    } catch (err) {
      const errorMessage = err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Failed to delete announcement';
      showToast.error(errorMessage);
      setConfirmDialog({ open: false, announcement: null });
    }
  };

  const handleClose = () => { setModalOpen(false); setEditing(null); };
  const handleSave = async (payload) => {
    setModalLoading(true);
    try {
      if (editing) await announcementService.update(editing.id, payload);
      else await announcementService.create(payload);
      await refresh(); setModalOpen(false); setEditing(null);
    } finally { setModalLoading(false); }
  };

  const filteredRows = useMemo(() => {
    if (!searchText.trim()) return rows;
    const query = searchText.toLowerCase();
    return rows.filter((row) =>
      [row.title, row.message, row.audience, row.target_name]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(query))
    );
  }, [rows, searchText]);

  return (
    <>
      <Box sx={{ display: 'flex' }}>
        <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} drawerWidth={drawerWidth} />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 0,
            ml: { xs: 0, md: `${0}px` },
            backgroundColor: 'background.default',
          }}
        >
          <Header onMenuClick={handleDrawerToggle} />
          <Box
            sx={{
              p: { xs: 2, sm: 2.5, md: 3 },
              mt: '64px',
              minHeight: 'calc(100vh - 64px)',
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h5" fontWeight={700}>
                Announcements
              </Typography>
              <Button variant="contained" startIcon={<CampaignIcon />} onClick={handleAdd}>
                Make Announcement
              </Button>
            </Stack>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                <CircularProgress />
              </Box>
            ) : rows.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                No announcements found
              </Typography>
            ) : (
              <>
                <TextField
                  fullWidth
                  placeholder="Search announcements..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  sx={{ mb: 2 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
                {filteredRows.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No announcements match your search.
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {filteredRows.map((row) => {
                  const message = row.message || '';
                  const isLong = message.length > 200;
                  const isExpanded = expandedMessages[row.id];
                  const displayMessage = isLong && !isExpanded ? `${message.substring(0, 200)}...` : message;
                  const formattedAudience = row.audience
                    ?.replace(/_/g, ' ')
                    .replace(/\b\w/g, (char) => char.toUpperCase()) || 'All';

                  return (
                    <Paper key={row.id} variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
                            {row.title}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', mb: isLong ? 1 : 0.5 }}
                          >
                            {displayMessage}
                          </Typography>
                          {isLong && (
                            <Button
                              size="small"
                              onClick={() => toggleMessage(row.id)}
                              sx={{
                                textTransform: 'none',
                                minWidth: 'auto',
                                px: 0.5,
                                fontSize: '0.75rem',
                              }}
                            >
                              {isExpanded ? 'View Less' : 'View More'}
                            </Button>
                          )}
                        </Box>
                        <Stack direction="row" spacing={1}>
                          <Chip size="small" label={formattedAudience} color={row.audience?.includes('teacher') ? 'info' : 'secondary'} />
                          <IconButton size="small" color="primary" onClick={() => handleEdit(row)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDelete(row)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </Stack>
                      <Stack direction="row" spacing={2} sx={{ mt: 1 }} flexWrap="wrap">
                        <Typography variant="caption" color="text.secondary">
                          Target: {row.target_name || 'All'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Sent: {row.created_at ? formatDate(row.created_at) : '—'}
                        </Typography>
                      </Stack>
                    </Paper>
                    );
                  })}
                  </Box>
                )}
              </>
            )}
          </Box>
        </Box>
      </Box>

      <AnnouncementForm
        open={modalOpen}
        onClose={handleClose}
        onSubmit={handleSave}
        initialData={editing}
        loading={modalLoading}
        teachers={teachers}
        parents={parents}
        title={editing ? 'Edit Announcement' : 'Add Announcement'}
      />

      <ConfirmationDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, announcement: null })}
        onConfirm={handleDeleteConfirm}
        title="Delete Announcement"
        message={`Are you sure you want to delete "${confirmDialog.announcement?.title || 'this announcement'}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmColor="error"
      />
    </>
  );
};

export default Announcements;


